import { getLayerRecommendation } from './layerLogic';
import { buildForecastKey, getCachedForecast, setCachedForecast } from './cache';

const BASE_URL = 'https://api.open-meteo.com/v1/forecast';

export const getLayerRecommendations = async ({ latitude, longitude, forecastHours = 0 }) => {
  // Cache lookup — saves 200-800ms when the same (lat, lng, forecastHours)
  // tuple was fetched in the last 10 minutes. forecastHours interpretation
  // drifts as wall-clock advances (forecastHours=3 means "+3 from now"); the
  // 10-min TTL bounds the drift to under a layer-category boundary.
  const cacheKey = buildForecastKey({ latitude, longitude, forecastHours, mode: 'today' });
  const cached = getCachedForecast(cacheKey);
  if (cached) return cached;
  try {
    // Dropped `&models=metno_seamless` 2026-05-26. The metno_seamless model
    // started 502-ing intermittently and a 502 from Open-Meteo doesn't carry
    // CORS headers, so the browser surfaces it as a CORS error and the whole
    // request hard-fails. Without an explicit model, Open-Meteo picks the
    // best model per region (best_match) — more reliable everywhere.
    const url = `${BASE_URL}?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,apparent_temperature,precipitation,wind_speed_10m&hourly=temperature_2m,apparent_temperature,precipitation,wind_speed_10m&past_days=2`;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineError = new Error("You appear to be offline. Check your connection and try again.");
      offlineError.kind = 'offline';
      throw offlineError;
    }

    let response;
    try {
      response = await fetch(url);
    } catch (networkError) {
      // fetch() throws on network failure / abort / CORS-less 5xx (upstream
      // 502 from Open-Meteo loses its CORS headers, surfacing as "Failed to
      // fetch" rather than a status code we can read). Don't blame the user's
      // connection — most often it's the forecast service struggling.
      if (networkError?.name === 'AbortError') throw networkError;
      const wrapped = new Error("Couldn't reach the forecast right now — the weather service may be having a moment. Try again in a bit.");
      wrapped.kind = 'network';
      wrapped.cause = networkError;
      throw wrapped;
    }
    if (!response.ok) {
      const err = new Error(
        response.status >= 500
          ? "The forecast service is having a moment. Try again in a few seconds."
          : `Couldn't load the forecast (${response.status}). Try again in a moment.`
      );
      err.status = response.status;
      throw err;
    }
    
    const data = await response.json();
    const now = new Date();
    const currentHourIndex = now.getHours() + (24 * 2);
    const targetIndex = forecastHours > 0 
      ? currentHourIndex + forecastHours 
      : forecastHours === -24 
        ? currentHourIndex - 24 
        : currentHourIndex;

    // Use apparent temperature for recommendations
    const apparentTemperature = forecastHours === 0 
      ? data?.current?.apparent_temperature 
      : data?.hourly?.apparent_temperature?.[targetIndex];
    
    // Get display temperature (actual temp at selected time)
    const displayTemperature = forecastHours === 0 
      ? data?.current?.temperature_2m 
      : data?.hourly?.temperature_2m?.[targetIndex];
    
    const precipitation = forecastHours === 0 
      ? data?.current?.precipitation 
      : data?.hourly?.precipitation?.[targetIndex] || 0;
    
    const windSpeed = forecastHours === 0 
      ? data?.current?.wind_speed_10m 
      : data?.hourly?.wind_speed_10m?.[targetIndex] || 0;

    // Calculate daily high/low from hourly actual temperatures
    const calculateDailyHighLow = () => {
      if (!data?.hourly?.temperature_2m) return { high: displayTemperature, low: displayTemperature };
      
      // Determine which day's data to use
      const today = new Date();
      const targetDate = new Date(today.getTime() + (forecastHours * 60 * 60 * 1000));
      
      // Get 24-hour period for the target date (simplified - using rough 24-hour window)
      const startIndex = Math.max(0, targetIndex - (targetIndex % 24));
      const endIndex = Math.min(data.hourly.temperature_2m.length - 1, startIndex + 23);
      
      const dayTemps = data.hourly.temperature_2m.slice(startIndex, endIndex + 1);
      
      return {
        high: Math.max(...dayTemps),
        low: Math.min(...dayTemps)
      };
    };

    const { high: dailyHigh, low: dailyLow } = calculateDailyHighLow();

    // Get recommendation using shared logic with background colors for UI
    const recommendation = getLayerRecommendation(apparentTemperature, precipitation, windSpeed, true);

    const result = {
      recommendation,
      temperature: {
        display: displayTemperature,      // Actual temp at selected time for big display
        apparent: apparentTemperature,    // Apparent temp for layer calculations
        dailyHigh: dailyHigh,            // Proper daily high
        dailyLow: dailyLow               // Proper daily low
      },
      conditions: {
        precipitation,
        windSpeed
      }
    };
    setCachedForecast(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error fetching layer recommendations:', error);
    throw error;
  }
};